import cn from 'classnames';
import { forwardRef } from 'react';

import { Button } from '../../../buttons';
import { useAccordionGroupContext } from '../accordion-group/accordion-group-context';
import { AccordionHeader } from '../accordion-header';

import type { ForwardedRef, ReactElement } from 'react';

import type { AccordionFilterHeaderProps } from './accordion-filter-header.types';

import './accordion-filter-header.scss';

/**
 * The `AccordionFilterHeader` component is a wrapper around `AccordionHeader`.
 * It has added functionality to support `Reset` button. Ideally should only be used with `plain` styling.
 *
 * ### Usage
 * Place the `AccordionFilterHeader` component inside an `Accordion` component to define the header of the accordion.
 *
 * ```tsx
 * import { AccordionGroup, Accordion, AccordionFilterHeader, AccordionContent } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <AccordionGroup>
 *       <Accordion value="banana-facts">
 *         <AccordionFilterHeader showReset onResetClick={(e) => handleResetClick(e)}>
 *           Banana facts
 *         </AccordionFilterHeader>
 *         <AccordionContent>
 *           Bananas are a great source of potassium and can help replenish electrolytes after exercise.
 *         </AccordionContent>
 *       </Accordion>
 *       <Accordion>...</Accordion>
 *       <Accordion>...</Accordion>
 *     </AccordionGroup>
 *   );
 * }
 * ```
 */
const AccordionFilterHeader = forwardRef(
  (props: AccordionFilterHeaderProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      children,
      className,
      onResetClick,
      showReset = false,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const accordionGroupContextValue = useAccordionGroupContext();
    if (accordionGroupContextValue === null) {
      throw new Error('No AccordionContext provided');
    }

    const classes = cn('rbui-accordion-filter-header', className);

    return (
      <div className={classes}>
        <AccordionHeader ref={forwardedRef} {...rest}>
          {children}
        </AccordionHeader>

        {showReset && (
          <Button
            className="rbui-accordion-filter-header__reset"
            onClick={(e) => {
              e.stopPropagation();
              onResetClick?.(e);
            }}
            type="inline"
          >
            Reset
          </Button>
        )}
      </div>
    );
  },
);

AccordionFilterHeader.displayName = 'AccordionFilterHeader';

export default AccordionFilterHeader;
